import { type HistogramRange } from '@solinkcorp/vd-common';
import HomeHelper from 'components/HomePage/HomeHelper';
import { getVideoSearchRetentionDate } from 'components/SidekickPage/VideoSearch/utils';
import { getLocalizedRangeLabel } from 'utils/histogramHelper';
import {
  ALARMS_URL,
  CLIPS_URL,
  DASHBOARD_URL,
  EVENTS_URL,
  getTabFromPathname,
  SETTINGS_URL_PREFIX,
  SIDEKICK_URL,
  VIDEO_URL_PREFIX,
} from 'utils/urls';
import { HISTOGRAM_RANGES, RANGE_DAY, RANGE_WEEK, TAB_DASHBOARD, TAB_VIDEO } from 'vd-constants';

export interface DateFilterRangeOption {
  label: number | string;
  value: HistogramRange;
}

type DateFilterLocale = Parameters<typeof getLocalizedRangeLabel>[0]['locale'];

export const DATE_PICKER_ROUTES = [
  ALARMS_URL,
  CLIPS_URL,
  DASHBOARD_URL,
  EVENTS_URL,
  SETTINGS_URL_PREFIX,
  VIDEO_URL_PREFIX,
  SIDEKICK_URL,
];

export const RANGE_PICKER_ROUTES = [DASHBOARD_URL, EVENTS_URL, SIDEKICK_URL];

export function isDateFilterRoute(pathname: string) {
  return DATE_PICKER_ROUTES.some((route) => pathname.startsWith(route));
}

/** Video page only. A playback end, a live-only user, or a kiosk without live hides Go Live. */
export function canGoLive(input: {
  pathname: string;
  videoPlaybackEndsAt?: unknown;
  kiosk: boolean;
  canPlayLiveInKiosk: boolean;
  liveViewOnly: boolean;
}): boolean {
  return (
    getTabFromPathname(input.pathname) === TAB_VIDEO &&
    !input.videoPlaybackEndsAt &&
    (!input.kiosk || input.canPlayLiveInKiosk) &&
    !input.liveViewOnly
  );
}

export function isRangePickerRoute(pathname: string) {
  return RANGE_PICKER_ROUTES.some((route) => pathname.startsWith(route));
}

/**
 * True on Events and Dashboard. Those chevrons step by the selected range.
 * Sidekick has a range selector, but its range always ends now, so its chevrons step by day.
 */
export function chevronsStepByRange(pathname: string): boolean {
  return pathname.startsWith(DASHBOARD_URL) || pathname.startsWith(EVENTS_URL);
}

/**
 * Apply writes the draft range unless a day click already forced Day and the
 * user left that unit selected. A later Week choice must still be written.
 */
export function shouldCommitDraftRange(input: {
  hasRangeOptions: boolean;
  dayPickSwitchesToDay: boolean;
  dayChanged: boolean;
  draftUnitIsDay: boolean;
}): boolean {
  if (!input.hasRangeOptions) {
    return false;
  }
  const dayPickStillOnDay = input.dayPickSwitchesToDay && input.dayChanged && input.draftUnitIsDay;
  return !dayPickStillOnDay;
}

/** Range units for a pathname. Undefined when the page has no range selector. */
export function buildRangeOptions(input: {
  pathname: string;
  locale: DateFilterLocale;
}): DateFilterRangeOption[] | undefined {
  if (!isRangePickerRoute(input.pathname)) {
    return;
  }

  const tabName = getTabFromPathname(input.pathname);
  if (tabName === TAB_DASHBOARD) {
    return HomeHelper.getRangeOptions({ locale: input.locale });
  }
  if (input.pathname.startsWith(SIDEKICK_URL)) {
    return [
      { label: input.locale.date.day, value: RANGE_DAY },
      { label: input.locale.histogram.lastSevenDays, value: RANGE_WEEK },
    ];
  }
  return HISTOGRAM_RANGES.map((range) => ({
    label: getLocalizedRangeLabel({ range, rangeRolling: false, locale: input.locale }),
    value: range,
  }));
}

export function getDateBounds(input: {
  pathname: string;
  timezoneId: string;
  videoPlaybackStartsAt?: string | number | Date | null;
  videoPlaybackEndsAt?: string | number | Date | null;
}): { minDate: Date | null; maxDate: Date | null } {
  let minDate: Date | null = null;
  if (input.pathname.startsWith(SIDEKICK_URL)) {
    minDate = getVideoSearchRetentionDate(input.timezoneId);
  } else if (input.videoPlaybackStartsAt) {
    minDate = new Date(input.videoPlaybackStartsAt);
  }

  const maxDate = input.videoPlaybackEndsAt ? new Date(input.videoPlaybackEndsAt) : null;
  return { minDate, maxDate };
}
